// src/pages/Story.js
import React, { useEffect, useMemo, useState } from "react";
import {
  collectionGroup,
  onSnapshot,
  query,
  orderBy,
  limit,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { getStorage, ref as storageRef, deleteObject } from "firebase/storage";
import { FaBookOpen, FaExclamationCircle } from "react-icons/fa";
import {
  FiEye,
  FiTrash2,
  FiUsers,
  FiMusic,
  FiSearch,
  FiFilter,
  FiClock,
  FiPlay,
  FiFileText,
  FiImage,
  FiVideo,
} from "react-icons/fi";

const COLORS = {
  text: "#0f172a",
  subtext: "#475569",
  border: "#e2e8f0",
  card: "#ffffff",
  primary: "#f20c53",
  secondary: "#fb6c33",
  success: "#16a34a",
  warn: "#b91c1c",
  muted: "#94a3b8",
  page: "#f5f6f8",
};

export default function Story() {
  const [tab, setTab] = useState("users"); // "users" | "artists"
  const [usersStories, setUsersStories] = useState([]);
  const [artistsStories, setArtistsStories] = useState([]);
  const [usersDupRemoved, setUsersDupRemoved] = useState(0);
  const [artistsDupRemoved, setArtistsDupRemoved] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // permissions (unchanged behavior)
  const [canView, setCanView] = useState(true);
  const [canDelete, setCanDelete] = useState(false);

  // viewer modal
  const [viewer, setViewer] = useState({ open: false, url: "" });

  // per-id deleting flag
  const [deleting, setDeleting] = useState({}); // { [id]: true }

  // toast
  const [toast, setToast] = useState("");

  // UI controls (purely UI-side)
  const [search, setSearch] = useState("");
  const [mediaFilter, setMediaFilter] = useState("All"); // All | Images | Videos | PDFs | Other
  const [sortKey, setSortKey] = useState("Newest"); // Newest | Oldest | Most Viewed

  useEffect(() => {
    const employee = JSON.parse(localStorage.getItem("employee"));
    const role = employee?.role;
    const perms = employee?.permissions || [];

    const allowedView = role === "super_admin" || perms.includes("view_story");
    setCanView(allowedView);

    const allowedDelete = role === "super_admin" || perms.includes("delete_story");
    setCanDelete(allowedDelete);

    if (!allowedView) return;

    // One stream over collection group "stories"
    const qStories = query(
      collectionGroup(db, "stories"),
      orderBy("createdAt", "desc"),
      limit(200)
    );

    const unsub = onSnapshot(
      qStories,
      (snap) => {
        const list = snap.docs.map((d) => normalizeStory(d, d.data()));
        const deduped = dedupeStories(list);
        // Split into Users vs Artists using fields present on the doc
        const users = [];
        const artists = [];
        for (const s of deduped.rows) {
          const isArtist =
            s.ownerType === "artist" ||
            s.isArtist === true ||
            s.role === "artist";
          if (isArtist) artists.push(s);
          else users.push(s);
        }
        setUsersStories(users);
        setArtistsStories(artists);
        const splitCounts = countDedupedByOwnerType(list);
        setUsersDupRemoved(splitCounts.usersRemoved);
        setArtistsDupRemoved(splitCounts.artistsRemoved);
        setLoading(false);
      },
      (e) => {
        console.error(e);
        const url = e?.message?.match(/https?:\/\/\S+/)?.[0];
        if (url) console.log("▶ Create required index here:", url);
        let msg = "Failed to load stories.";
        if (e?.code === "permission-denied") {
          msg = "Permission denied by Firestore rules.";
        }
        setErr(msg);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const activeStories = useMemo(
    () => (tab === "users" ? usersStories : artistsStories),
    [tab, usersStories, artistsStories]
  );

  const filteredSorted = useMemo(() => {
    let arr = [...activeStories];

    // search
    const q = search.trim().toLowerCase();
    if (q) {
      arr = arr.filter((s) => {
        const hay = `${s.title || ""} ${s.author || ""}`.toLowerCase();
        return hay.includes(q);
      });
    }

    // media type filter
    if (mediaFilter !== "All") {
      arr = arr.filter((s) => mediaType(s.media) === mediaFilter);
    }

    // sort
    if (sortKey === "Newest") {
      arr.sort((a, b) => dateVal(b.createdAt) - dateVal(a.createdAt));
    } else if (sortKey === "Oldest") {
      arr.sort((a, b) => dateVal(a.createdAt) - dateVal(b.createdAt));
    } else if (sortKey === "Most Viewed") {
      arr.sort((a, b) => numVal(b.views) - numVal(a.views));
    }

    return arr;
  }, [activeStories, search, mediaFilter, sortKey]);

  async function handleDelete(story) {
    if (!canDelete || !story) return;
    const ok = window.confirm("Delete this story permanently? This can’t be undone.");
    if (!ok) return;
    const opKey = story.docPath || story.id;

    try {
      setDeleting((m) => ({ ...m, [opKey]: true }));

      // 1) Delete storage file if possible (prefer stored path if your docs have it)
      try {
        if (story.storagePath) {
          const storage = getStorage();
          await deleteObject(storageRef(storage, story.storagePath));
        } else if (story.media && isFirebaseStorageUrl(story.media)) {
          const storage = getStorage();
          // ref(...) accepts gs:// or HTTPS download URLs
          await deleteObject(storageRef(storage, story.media));
        }
      } catch (e) {
        console.warn("Storage deletion failed (continuing to delete doc):", e?.message || e);
      }

      // 2) Delete Firestore doc
      await deleteDoc(story.ref);

      setToast("Story deleted.");
      setTimeout(() => setToast(""), 2000);
    } catch (e) {
      console.error(e);
      setToast("Delete failed.");
      setTimeout(() => setToast(""), 2000);
    } finally {
      setDeleting((m) => ({ ...m, [opKey]: false }));
    }
  }

  function openViewer(url) {
    if (!url) return;
    setViewer({ open: true, url });
  }
  function closeViewer() {
    setViewer((v) => ({ ...v, open: false }));
  }

  if (!canView) {
    return (
      <div style={deniedWrap}>
        <div style={deniedCard}>
          <FaExclamationCircle size={36} color="#e53935" />
          <h3 style={{ margin: "1rem 0 0" }}>Access Denied</h3>
          <p style={{ marginTop: 8, color: COLORS.subtext }}>
            You do not have permission to view Stories.
          </p>
        </div>
      </div>
    );
  }

  const totals = {
    users: usersStories.length,
    artists: artistsStories.length,
    all: usersStories.length + artistsStories.length,
    removed: usersDupRemoved + artistsDupRemoved,
  };

  return (
    <div className="aa-pro-page" style={{ padding: "1.5rem", fontFamily: "Inter, system-ui, sans-serif", background: COLORS.page }}>
      {/* Hero */}
      <div className="aa-pro-hero" style={hero}>
        <div style={heroLeft}>
          <div className="aa-pro-hero-icon" style={heroIcon}>
            <FaBookOpen />
          </div>
          <div>
            <h2 className="aa-pro-title" style={heroTitle}>User & Artist Stories</h2>
            <p className="aa-pro-subtitle" style={heroSub}>Browse, filter, and preview stories with a cleaner media review surface.</p>
          </div>
        </div>
        <div className="aa-pro-metrics" style={heroStats}>
          <Stat label="Total" value={totals.all} />
          <Stat label="Users" value={totals.users} />
          <Stat label="Artists" value={totals.artists} />
        </div>
      </div>

      {/* Tabs */}
      <div className="aa-pro-segment" style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <button
          onClick={() => setTab("users")}
          style={{
            ...tabBtn,
            background:
              tab === "users"
                ? `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`
                : "#fff",
            color: tab === "users" ? "#fff" : COLORS.text,
          }}
          title="Users Stories"
        >
          <FiUsers />
          <span style={{ fontWeight: 800 }}>Users Stories</span>
          <span style={pillCount}>{totals.users}</span>
        </button>
        <button
          onClick={() => setTab("artists")}
          style={{
            ...tabBtn,
            background:
              tab === "artists"
                ? `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`
                : "#fff",
            color: tab === "artists" ? "#fff" : COLORS.text,
          }}
          title="Artists Stories"
        >
          <FiMusic />
          <span style={{ fontWeight: 800 }}>Artists Stories</span>
          <span style={pillCount}>{totals.artists}</span>
        </button>
      </div>

      {/* Controls */}
      <div className="aa-pro-toolbar" style={controlsRow}>
        <div style={searchBox}>
          <FiSearch style={{ opacity: 0.7 }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title or author…"
            style={searchInput}
          />
        </div>

        <div style={controlsRight}>
          <div style={controlGroup}>
            <FiFilter />
            <select value={mediaFilter} onChange={(e) => setMediaFilter(e.target.value)} style={select}>
              <option>All</option>
              <option>Images</option>
              <option>Videos</option>
              <option>PDFs</option>
              <option>Other</option>
            </select>
          </div>
          <div style={controlGroup}>
            <FiClock />
            <select value={sortKey} onChange={(e) => setSortKey(e.target.value)} style={select}>
              <option>Newest</option>
              <option>Oldest</option>
              <option>Most Viewed</option>
            </select>
          </div>
        </div>
      </div>

      {totals.removed > 0 && (
        <div style={dedupeNote}>
          Hidden duplicate stories: {totals.removed} (Users: {usersDupRemoved}, Artists: {artistsDupRemoved})
        </div>
      )}

      {loading && (
        <div style={skeletonGrid}>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} style={skeletonCard} />
          ))}
        </div>
      )}
      {err && <div style={{ color: COLORS.warn, marginTop: 8 }}>{err}</div>}

      {/* Grid */}
      {!loading && !err && (
        <div
          className="aa-pro-gallery-grid"
          style={{
            display: "grid",
            gap: "1.2rem",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
          }}
        >
          {filteredSorted.map((s) => (
            <StoryCard
              key={s.docPath || `${s.ownerType || "story"}:${s.id}`}
              story={s}
              canDelete={canDelete}
              deleting={!!deleting[s.docPath || s.id]}
              onView={() => openViewer(s.media)}
              onDelete={() => handleDelete(s)}
            />
          ))}

          {filteredSorted.length === 0 && (
            <div
              style={{
                border: `1px dashed ${COLORS.border}`,
                borderRadius: 12,
                padding: 16,
                color: COLORS.subtext,
                background: "#fff",
              }}
            >
              No stories match your filters.
            </div>
          )}
        </div>
      )}

      {/* Viewer modal */}
      {viewer.open && (
        <Modal onClose={closeViewer} title="Preview">
          <MediaViewer url={viewer.url} />
        </Modal>
      )}

      {/* Toast */}
      {toast && <div style={toastBox}>{toast}</div>}
    </div>
  );
}

/* ────────────────────────────────────────────────────────── */
/* Components                                                 */
/* ────────────────────────────────────────────────────────── */

function StoryCard({ story, canDelete, deleting, onView, onDelete }) {
  const { title, author, media, views, createdAt, expiresAt } = story;
  const mType = mediaType(media);
  const badge = mediaBadge(mType);

  return (
    <div
      style={{
        background: COLORS.card,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 14,
        padding: 12,
        boxShadow: "0 6px 14px rgba(0,0,0,.06)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Media (actions overlay INSIDE, on top) */}
      {media ? (
        <div
          onClick={onView}
          title="View"
          style={{
            border: `1px solid ${COLORS.border}`,
            borderRadius: 10,
            overflow: "hidden",
            cursor: "pointer",
            position: "relative",
          }}
        >
          <MediaThumb url={media} />

          {/* Overlay actions (always on top) */}
          <div style={overlayActions} onClick={(e) => e.stopPropagation()}>
            <button onClick={onView} title="View" style={iconBtn}>
              <FiEye />
            </button>
            {canDelete && (
              <button
                onClick={onDelete}
                title="Delete"
                style={{ ...iconBtn, color: deleting ? COLORS.muted : COLORS.warn }}
                disabled={deleting}
              >
                <FiTrash2 />
              </button>
            )}
          </div>

          {/* Overlay badge */}
          <div style={mediaBadgeStyle}>
            {badge.icon} <span>{badge.label}</span>
          </div>

          {/* Play overlay for video */}
          {mType === "Videos" && (
            <div style={playOverlay}>
              <FiPlay />
            </div>
          )}
        </div>
      ) : (
        <div
          style={{
            border: `1px dashed ${COLORS.border}`,
            borderRadius: 10,
            padding: 12,
            color: COLORS.subtext,
            textAlign: "center",
          }}
        >
          No media
        </div>
      )}

      {/* Title / Author */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
        <Avatar name={author} />
        <div>
          <div style={{ fontWeight: 900, color: COLORS.text }}>{title}</div>
          <div style={{ color: COLORS.subtext, fontSize: 12 }}>by {author}</div>
        </div>
      </div>

      {/* Meta */}
      <div style={{ display: "flex", gap: 12, marginTop: 8, fontSize: 12, color: COLORS.subtext, flexWrap: "wrap" }}>
        <span>Views: <b>{views ?? "—"}</b></span>
        <span>Posted: {formatDate(createdAt)}</span>
        {expiresAt && <span>Expires: {formatDate(expiresAt)}</span>}
      </div>
    </div>
  );
}

function Avatar({ name }) {
  const initial = (name || "U").trim().charAt(0).toUpperCase();
  return (
    <div
      style={{
        width: 42,
        height: 42,
        borderRadius: "50%",
        border: `1px solid ${COLORS.border}`,
        background: "#fff",
        display: "grid",
        placeItems: "center",
        fontWeight: 900,
        color: COLORS.text,
      }}
      title={name}
    >
      {initial}
    </div>
  );
}

function MediaThumb({ url }) {
  const isImg = /\.(png|jpe?g|webp|gif)(\?|$)/i.test(url);
  const isVideo = /\.(mp4|webm|ogg)(\?|$)/i.test(url);
  return (
    <>
      {isImg ? (
        <img
          src={url}
          alt="media"
          style={{
            width: "100%",
            height: 172,
            objectFit: "cover",
            display: "block",
            position: "relative",
            zIndex: 1,
          }}
        />
      ) : isVideo ? (
        <video
          src={url}
          style={{
            width: "100%",
            height: 172,
            objectFit: "cover",
            display: "block",
            position: "relative",
            zIndex: 1,
          }}
          muted
        />
      ) : (
        <div style={{ padding: 18, color: COLORS.subtext, fontSize: 13, textAlign: "center" }}>
          Tap the eye icon to open file.
        </div>
      )}
    </>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div style={modalBackdrop}>
      <div style={modalCard}>
        <div style={modalHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#fff", fontWeight: 900 }}>
            <div style={modalHeaderIcon}><FaBookOpen /></div>
            <div>{title}</div>
          </div>
          <button onClick={onClose} style={modalCloseBtn} aria-label="Close">
            ✕
          </button>
        </div>
        <div style={{ padding: 12 }}>{children}</div>
      </div>
    </div>
  );
}

function MediaViewer({ url }) {
  const isPdf = /\.pdf(\?|$)/i.test(url);
  const isImg = /\.(png|jpe?g|webp|gif)(\?|$)/i.test(url);
  const isVideo = /\.(mp4|webm|ogg)(\?|$)/i.test(url);

  if (isPdf) {
    return (
      <iframe
        title="PDF"
        src={url}
        style={{ width: "100%", height: 520, border: `1px solid ${COLORS.border}`, borderRadius: 8 }}
      />
    );
  }
  if (isImg) {
    return (
      <img
        alt="document"
        src={url}
        style={{
          width: "100%",
          maxHeight: 520,
          objectFit: "contain",
          border: `1px solid ${COLORS.border}`,
          borderRadius: 8,
        }}
      />
    );
  }
  if (isVideo) {
    return (
      <video
        controls
        src={url}
        style={{ width: "100%", maxHeight: 520, border: `1px solid ${COLORS.border}`, borderRadius: 8 }}
      />
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" style={linkBtn}>
      Open file
    </a>
  );
}

function Stat({ label, value }) {
  return (
    <div
      className="aa-pro-metric"
      style={{
        background: "#fff",
        border: `1px solid ${COLORS.border}`,
        borderRadius: 10,
        padding: "8px 12px",
        minWidth: 90,
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: 12, color: COLORS.subtext }}>{label}</div>
      <div style={{ fontWeight: 900, color: COLORS.text }}>{value}</div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────── */
/* Helpers                                                    */
/* ────────────────────────────────────────────────────────── */

function normalizeStory(docSnap, x = {}) {
  const id = docSnap.id;
  const ref = docSnap.ref;

  const title = x.title || x.heading || x.caption || "Story";
  const author = x.author || x.authorName || x.userName || x.artistName || x.name || "—";
  const summary = x.summary || x.text || x.description || "";
  const media =
    x.media ||
    x.mediaUrl ||
    x.imageUrl ||
    x.videoUrl ||
    x.fileUrl ||
    "";
  const views = x.views ?? x.viewCount ?? x.seen ?? null;

  return {
    id,
    ref, // exact path for deletes
    docPath: ref?.path || "",
    title,
    author,
    summary,
    media,
    views,
    createdAt: x.createdAt || null,
    expiresAt: x.expiresAt || null,
    ownerId: x.ownerId || null,
    ownerType: x.ownerType || null,
    storyId: x.storyId || x.story_id || null,
    sourceStoryId: x.sourceStoryId || x.originalStoryId || null,
    isArtist: x.isArtist ?? null,
    role: x.role || null,
    storagePath: x.storagePath || null, // optional: if you store it
  };
}

function dedupeStories(rows = []) {
  const seen = new Set();
  const deduped = [];

  for (const row of rows) {
    const key = dedupeStoryKey(row);
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(row);
  }

  return { rows: deduped, removedCount: Math.max(0, rows.length - deduped.length) };
}

function countDedupedByOwnerType(rows = []) {
  const seenUsers = new Set();
  const seenArtists = new Set();
  let usersRemoved = 0;
  let artistsRemoved = 0;

  for (const row of rows) {
    const isArtist =
      row.ownerType === "artist" ||
      row.isArtist === true ||
      String(row.role || "").toLowerCase() === "artist";
    const key = dedupeStoryKey(row);
    if (isArtist) {
      if (seenArtists.has(key)) artistsRemoved += 1;
      else seenArtists.add(key);
    } else {
      if (seenUsers.has(key)) usersRemoved += 1;
      else seenUsers.add(key);
    }
  }

  return { usersRemoved, artistsRemoved };
}

function dedupeStoryKey(row = {}) {
  const stamp = dateVal(row.createdAt);
  const ownerType = String(row.ownerType || "unknown");
  const ownerId = String(row.ownerId || row.author || "");
  const title = String(row.title || "").trim().toLowerCase();
  const media = normalizeStoryMediaUrl(row.media || "");
  const explicit = String(row.storyId || row.sourceStoryId || "").trim();
  if (explicit) return `id:${ownerType}:${ownerId}:${explicit}`;
  return `${ownerType}|${ownerId}|${title}|${media}|${stamp}`;
}

function normalizeStoryMediaUrl(url = "") {
  const trimmed = String(url || "").trim().toLowerCase();
  if (!trimmed) return "";
  return trimmed.split("?")[0];
}

function isFirebaseStorageUrl(url = "") {
  if (!url) return false;
  return /^gs:\/\//i.test(url) || /^https?:\/\/firebasestorage\.googleapis\.com\//i.test(url);
}

function mediaType(url = "") {
  if (!url) return "Other";
  if (/\.pdf(\?|$)/i.test(url)) return "PDFs";
  if (/\.(png|jpe?g|webp|gif)(\?|$)/i.test(url)) return "Images";
  if (/\.(mp4|webm|ogg)(\?|$)/i.test(url)) return "Videos";
  return "Other";
}

function mediaBadge(type) {
  if (type === "Images") return { icon: <FiImage />, label: "Image" };
  if (type === "Videos") return { icon: <FiVideo />, label: "Video" };
  if (type === "PDFs") return { icon: <FiFileText />, label: "PDF" };
  return { icon: <FiFileText />, label: "File" };
}

function dateVal(ts) {
  try {
    if (!ts) return 0;
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.getTime() || 0;
  } catch {
    return 0;
  }
}

function numVal(v) {
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
}

function formatDate(ts) {
  try {
    if (!ts) return "—";
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString();
  } catch {
    return "—";
  }
}

/* ────────────────────────────────────────────────────────── */
/* Styles                                                     */
/* ────────────────────────────────────────────────────────── */
const hero = {
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
  color: "#fff",
  padding: "14px 16px",
  borderRadius: 14,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  boxShadow: "0 14px 26px rgba(0,0,0,0.12)",
  marginBottom: 14,
};
const heroLeft = { display: "flex", alignItems: "center", gap: 12 };
const heroIcon = {
  width: 42,
  height: 42,
  borderRadius: 10,
  display: "grid",
  placeItems: "center",
  background: "rgba(255,255,255,0.12)",
  border: "1px solid rgba(255,255,255,0.35)",
};
const heroTitle = { margin: 0, fontSize: 20, fontWeight: 900, lineHeight: 1.1 };
const heroSub = { margin: "4px 0 0", opacity: 0.92, fontSize: 13 };
const heroStats = { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" };

const controlsRow = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  alignItems: "center",
  marginBottom: 12,
};
const dedupeNote = {
  border: `1px solid ${COLORS.border}`,
  background: "#fff7ed",
  color: COLORS.text,
  borderRadius: 10,
  padding: "8px 12px",
  marginBottom: 12,
  fontSize: 13,
  fontWeight: 700,
};
const searchBox = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  padding: "10px 12px",
  background: "#fff",
  minWidth: 260,
  flex: 1,
};
const searchInput = {
  border: "none",
  outline: "none",
  flex: 1,
  fontSize: 14,
};
const controlsRight = { display: "flex", gap: 10, flexWrap: "wrap" };
const controlGroup = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  padding: "8px 10px",
  background: "#fff",
};
const select = {
  border: "none",
  outline: "none",
  background: "transparent",
  fontSize: 14,
  paddingRight: 4,
  cursor: "pointer",
};

const tabBtn = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 999,
  padding: "8px 12px",
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  cursor: "pointer",
  boxShadow: "0 4px 10px rgba(0,0,0,.06)",
};

const pillCount = {
  marginLeft: 6,
  padding: "0 8px",
  height: 22,
  lineHeight: "22px",
  borderRadius: 999,
  background: "#fff",
  color: COLORS.text,
  border: `1px solid ${COLORS.border}`,
  fontSize: 12,
  fontWeight: 800,
};

const iconBtn = {
  width: 34,
  height: 34,
  display: "grid",
  placeItems: "center",
  borderRadius: 8,
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
  cursor: "pointer",
};

const overlayActions = {
  position: "absolute",
  top: 8,
  right: 8,
  display: "flex",
  gap: 6,
  zIndex: 5,
  pointerEvents: "auto",
};

const linkBtn = {
  border: "none",
  background: "transparent",
  color: COLORS.primary,
  fontWeight: 800,
  cursor: "pointer",
  textDecoration: "none",
};

const mediaBadgeStyle = {
  position: "absolute",
  top: 8,
  left: 8,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  background: "rgba(255,255,255,0.92)",
  color: COLORS.text,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 999,
  padding: "4px 10px",
  fontWeight: 800,
  fontSize: 12,
  zIndex: 4,
};

const playOverlay = {
  position: "absolute",
  inset: 0,
  display: "grid",
  placeItems: "center",
  color: "#fff",
  fontSize: 28,
  background: "linear-gradient(0deg, rgba(0,0,0,0.35), rgba(0,0,0,0.2))",
  pointerEvents: "none",
  zIndex: 2,
};

const toastBox = {
  position: "fixed",
  bottom: 16,
  right: 16,
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
  border: "none",
  color: "#fff",
  borderRadius: 10,
  padding: "10px 12px",
  boxShadow: "0 16px 26px rgba(0,0,0,.2)",
  fontWeight: 800,
  zIndex: 60,
};

const modalBackdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,.45)",
  display: "grid",
  placeItems: "center",
  zIndex: 60,
  padding: 16,
};

const modalCard = {
  width: "100%",
  maxWidth: 860,
  background: "#fff",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 12,
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(0,0,0,.25)",
};

const modalHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: 12,
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
};
const modalHeaderIcon = {
  width: 32,
  height: 32,
  borderRadius: 8,
  border: "1px solid rgba(255,255,255,0.4)",
  background: "rgba(255,255,255,0.12)",
  display: "grid",
  placeItems: "center",
};
const modalCloseBtn = {
  width: 34,
  height: 34,
  display: "grid",
  placeItems: "center",
  borderRadius: 8,
  border: "1px solid rgba(255,255,255,0.45)",
  background: "transparent",
  color: "#fff",
  cursor: "pointer",
};

const deniedWrap = {
  position: "fixed",
  inset: 0,
  backdropFilter: "blur(5px)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 999,
};
const deniedCard = {
  backgroundColor: "#fff",
  padding: "2rem",
  borderRadius: "10px",
  textAlign: "center",
  boxShadow: "0 0 20px rgba(0,0,0,0.15)",
  maxWidth: "400px",
};

const skeletonGrid = {
  display: "grid",
  gap: "1.2rem",
  gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
};
const skeletonCard = {
  height: 248,
  background: `linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 37%, #f3f4f6 63%)`,
  backgroundSize: "400% 100%",
  borderRadius: 14,
  animation: "shimmer 1.6s infinite",
};

/* Animated shimmer (inline keyframes) */
if (typeof document !== "undefined" && !document.getElementById("story-shimmer-style")) {
  const kf = document.createElement("style");
  kf.id = "story-shimmer-style";
  kf.textContent = `
    @keyframes shimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
  `;
  document.head.appendChild(kf);
}
